import React, { useCallback, useEffect, useMemo, useState } from 'react'
import * as Sentry from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { Outlet, useNavigate } from 'react-router-dom'
import { SidebarNav } from './SidebarNav'
import { RouteGuard } from './RouteGuard'
import { CanonicalTopbar, SidebarProvider, SidebarInset, useSidebar, type SearchResultItem } from '@appic/ui-components'
import { isAllowed as isDocSearchAllowed } from '@/components/atc/DocsPageConstants'
import { SubpageTabsHeader } from '@/components/navigation/SubpageTabsHeader'
import { useCurrentUser } from '@/lib/auth/UserContext'
import { getUserByEmail } from '@/lib/api/users'
import ProtectedRoute from '@/lib/auth/ProtectedRoute'
import { usePermissions, useConflictDetection, useRealtimeSync, useAuthAuditTrail, RealtimeStatusContext, useWacklerLens, useDocSearch, useDebounce, useAgentInbox, useMorningSync, useSyncOnReconnect, useAgentSignalsRealtime } from '@appic/hooks'
import { FileText } from 'lucide-react'
import { RealtimeConnectionToast } from '../../components/common/RealtimeConnectionToast'
import { NotificationsPanel } from '../../components/common/NotificationsPanel'
import { usePushNotifications } from './usePushNotifications'
import { DebugReport } from '../../components/common/DebugReport'
import { BuildRefBadge } from '../../components/common/BuildRefBadge'
import { registerOfflineHandlers, startOfflineQueue, startReachabilityMonitor, startUnloadFlush } from '@appic/lib'

// Register offline queue handlers once at module load. The prequal-specific
// offline handler was removed 2026-04-20 — it replayed POST /v1/prequal/
// results, which is unreachable since raider-intel dropped the prequal_*
// views. Greenfield path (/v1/calling/calls) is SL-handled; no FE offline
// replay layer per MO-CALLING-GREENFIELD-FE v3.
registerOfflineHandlers()
startOfflineQueue()

// Last-gasp durable capture as the tab is hidden/killed (FR-FE-IRONCLAD-TELEMETRY-OUTBOX):
// best-effort beacon of the still-pending outbox to the off-device sink on pagehide, so a
// report submitted seconds before the app is backgrounded is recoverable even if the 30s
// flush never runs. Never deletes an entry — only a confirmed replay ack does.
startUnloadFlush()

// The connectivity SSoT. Probes the service layer on an interval, on tab/PWA resume
// and whenever the OS link-layer hint flips — because that hint is a rumour, not a
// verdict, and believing it cost eleven days of field data (BUG-2026-07-14-VC559).
// Until a probe answers, reachability is `unknown` and every write is ATTEMPTED.
startReachabilityMonitor()

interface AppLayoutProps {
  children?: React.ReactNode
}

// Inner component that uses SidebarProvider context
const AppLayoutInner: React.FC<{ children?: React.ReactNode; userProfile: { name: string; email: string; role: string } | null; email: string | null }> = ({ children, userProfile, email }) => {
  const { toggleSidebar } = useSidebar()
  const { isSuperadmin } = usePermissions()
  const navigate = useNavigate()
  const { data: inboxMessages } = useAgentInbox(undefined, { enabled: isSuperadmin })
  usePushNotifications({ enabled: isSuperadmin })

  const commanderUnread = useMemo(() => {
    if (!inboxMessages) return []
    return inboxMessages.filter((s) => s.target === 'commander' && !s.consumed)
  }, [inboxMessages])

  const unreadCount = commanderUnread.length

  const recentUnread = useMemo(() => {
    return [...commanderUnread].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    ).slice(0, 20)
  }, [commanderUnread])
  const [bugReportOpen, setBugReportOpen] = React.useState(false)
  const { active: lensActive, toggle: toggleLens } = useWacklerLens()

  // Global topbar search — three behaviors:
  //   1. As-you-type: each keystroke fires `useDocSearch` (debounced 300ms),
  //      results render inline in the overlay popover.
  //   2. Click result → SPA-navigate to `/atc/docs/:id` (the full-width reader).
  //   3. Submit (Enter / Suchen): navigate to `/atc/docs?q=…` for the full
  //      Doc-Suche page (existing deep-link behavior, kept as "show all").
  // RBAC: gated to email-allowlisted operators (Commander + Simon today).
  // Non-allowed users see the overlay but no results fire (gated upstream).
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebounce(searchQuery, 300)
  const trimmedSearchQuery = debouncedSearchQuery.trim()
  const docSearchEnabled = isDocSearchAllowed(email)
  const docSearchQuery = useDocSearch({
    q: docSearchEnabled ? trimmedSearchQuery : '',
    limit: 8,
  })
  const searchResults = useMemo<SearchResultItem[] | undefined>(() => {
    if (!docSearchEnabled || !trimmedSearchQuery) return undefined
    const docs = docSearchQuery.data?.data ?? []
    return docs.map((doc) => ({
      id: doc.id,
      // Search hit `title` is a nullable projection (CommanderDocSearchRow);
      // fall back to the doc id — the real identity — never a blank label.
      title: doc.title ?? doc.id,
      snippet: [doc.type, doc.status].filter(Boolean).join(' · '),
      url: `/atc/docs/${encodeURIComponent(doc.id)}`,
      icon: <FileText className="h-4 w-4" />,
    }))
  }, [docSearchEnabled, trimmedSearchQuery, docSearchQuery.data])
  const handleGlobalSearch = useCallback((query: string) => {
    const q = query.trim()
    if (!q) return
    if (!docSearchEnabled) return
    navigate(`/atc/docs?q=${encodeURIComponent(q)}`)
  }, [docSearchEnabled, navigate])
  const handleSearchResultClick = useCallback((item: SearchResultItem) => {
    if (item.url) navigate(item.url)
  }, [navigate])

  // Bridge: child components (e.g. CockpitTopBar inside PreQualCalling, which
  // overlays the app topbar) dispatch 'appic:open-bug-report' to open the
  // shared modal without prop-drilling state through grandfathered files.
  React.useEffect(() => {
    const open = () => setBugReportOpen(true)
    window.addEventListener('appic:open-bug-report', open)
    return () => window.removeEventListener('appic:open-bug-report', open)
  }, [])

  // PWA app-lifecycle: when the user backgrounds the installed PWA on
  // iOS / Android (home screen, app switcher) and returns, react-query's
  // refetchOnWindowFocus doesn't always fire — `visibilitychange` is
  // the reliable signal. Invalidate live data so the operator sees a
  // fresh fleet state on resume instead of stale rows. Scoped to
  // bridge-relevant queries to avoid blast radius.
  const qc = useQueryClient()
  React.useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return
      void qc.invalidateQueries({ queryKey: ['agents-v1'] })
      void qc.invalidateQueries({ queryKey: ['daemon-status'] })
      void qc.invalidateQueries({ queryKey: ['inbox'] })
      void qc.invalidateQueries({ queryKey: ['commander-escalations'] })
      void qc.invalidateQueries({ queryKey: ['commander-prs'] })
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [qc])

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <CanonicalTopbar
        quickActionsCount={0}
        helpUrl="/scout/hilfe"
        showSearch={isSuperadmin}
        onSearch={handleGlobalSearch}
        onSearchQueryChange={setSearchQuery}
        searchResults={searchResults}
        onSearchResultClick={handleSearchResultClick}
        notifications={isSuperadmin ? unreadCount : 0}
        showNotifications={isSuperadmin}
        notificationsPanel={isSuperadmin ? <NotificationsPanel signals={recentUnread} /> : undefined}
        showHelp={false}
        showSidebarToggle={isSuperadmin}
        showAdminMenuItems={isSuperadmin}
        user={userProfile ? {
          id: email || '1',
          name: userProfile.name,
          email: userProfile.email,
          role: userProfile.role,
          department: '',
          status: 'online' as const,
          plan: 'pro' as const,
          notifications: isSuperadmin ? unreadCount : 0,
          verified: true,
          location: '',
          phone: '',
          joinDate: ''
        } : undefined}
        onSidebarToggle={toggleSidebar}
        onBugReport={() => setBugReportOpen(true)}
        wacklerLens={{
          active: lensActive,
          onToggle: toggleLens,
        }}
      />
      <RealtimeConnectionToast />
      <DebugReport open={bugReportOpen} onClose={() => setBugReportOpen(false)} />
      {/* Always-visible-but-unobtrusive build ref — lets an operator read the
          exact deployed image back to support (commit-sha is unreliable here;
          this shows the Cloud Build id). Fixed bottom-left, behind dialogs. */}
      <BuildRefBadge />

      {/* Main Content with Sidebar */}
      <div className="flex w-full overflow-x-hidden flex-1 min-h-0">
        {isSuperadmin && <SidebarNav />}

        <SidebarInset className="flex-1 min-w-0 flex flex-col overflow-y-auto h-full overscroll-contain">
          <SubpageTabsHeader />
          {/* Content wrapper must size to its natural content height so the
              SidebarInset (the overflow-y-auto scroll container) can scroll.
              `flex-1` (flex-basis:0 + grow) clamped this wrapper to the
              remaining viewport height, so overflowing page content did not
              extend the scroller's scrollHeight — only the sticky
              SubpageTabsHeader's tiny range scrolled, leaving the main
              content section unscrollable (BUG-FE-BRIDGE-MULTI-PAGE-SCROLL). */}
          <div className="min-w-0 content-padding-x">
            <RouteGuard>
              {children || <Outlet />}
            </RouteGuard>
          </div>
        </SidebarInset>
      </div>
    </div>
  )
}

export const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const { email, role } = useCurrentUser()
  const [userProfile, setUserProfile] = React.useState<{ name: string; email: string; role: string } | null>(null)

  // Morning Sync: day-pack prefetch into IndexedDB on app open. useSyncOnReconnect
  // registers offline-queue flush on browser `online`. S6 removed the sync status
  // strip from chrome; these hooks must still mount for offline correctness.
  useMorningSync(email, role)
  useSyncOnReconnect()

  // Conflict detection: show toast when offline queue hits server-side conflicts
  useConflictDetection()
  // Auth audit trail: log session expiry, re-handoff, 401 events to scout_activity_log
  useAuthAuditTrail()
  // Realtime: invalidate React Query cache when Supabase publishes changes
  const { status: realtimeStatus } = useRealtimeSync()
  useAgentSignalsRealtime()
  const realtimeCtx = useMemo(() => ({
    status: realtimeStatus,
    isLive: realtimeStatus === 'connected',
    isReconnecting: realtimeStatus === 'reconnecting',
  }), [realtimeStatus])

  // App load, once a session user is known (the confirmed sink needs one): visit-report drafts THIS USER parked on
  // this device — a visit replaced while "v2 saved, v8 owed" — are captured off-device. Another user's parked drafts
  // are not touched (the drain reads the signed-in session itself). Never throws; removes only the confirmed. Loaded
  // on demand: the drain belongs to the scout pages' chunk and must not ride the layout's eager bundle.
  useEffect(() => {
    if (!email) return
    void import('@/pages/scout/parkedDrafts.drain').then((drain) => drain.drainParkedDrafts()).catch((err: unknown) => {
      console.error('[AppLayout] the parked visit-report drafts could not be drained at app load (the drain module did not load)', err)
    })
  }, [email])

  useEffect(() => {
    if (email) {
      // Set Sentry user context for all error reports
      Sentry.setUser({ email })
      getUserByEmail(email).then(profile => {
        if (profile) {
          Sentry.setUser({ email: profile.email, username: profile.fullName || email.split('@')[0] })
          setUserProfile({
            name: profile.fullName || email.split('@')[0],
            email: profile.email,
            role: profile.role === 'superadmin' ? 'Administrator' : profile.role === 'sales_manager' ? 'Manager' : 'User'
          })
        }
      }).catch(() => {
        // Fallback to email if profile fetch fails
        setUserProfile({
          name: email.split('@')[0],
          email: email,
          role: 'User'
        })
      })
    } else {
      Sentry.setUser(null)
      setUserProfile(null)
    }
  }, [email])

  return (
    // h-screen-d (= 100dvh, dynamic viewport height) instead of h-screen
    // (= 100dvh) so the app shrinks to the visible viewport on mobile/PWA
    // when browser chrome (URL bar, home indicator) reduces the available
    // height. Without this the bottom of the layout is clipped below
    // the visible viewport and chat-grammar composers go below the
    // fold. Working Doc §2 #8.
    <div className="h-screen-d flex flex-col overflow-hidden">
      {/* ProtectedRoute wraps everything - topbar only shows when authenticated */}
      <ProtectedRoute>
        <RealtimeStatusContext.Provider value={realtimeCtx}>
          {/* SidebarProvider manages sidebar state */}
          <SidebarProvider defaultOpen={false}>
            <AppLayoutInner userProfile={userProfile} email={email}>
              {children}
            </AppLayoutInner>
          </SidebarProvider>
        </RealtimeStatusContext.Provider>
      </ProtectedRoute>
    </div>
  )
}